import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Upload, Search, Download, Check, X, Trash2, Gift, AlertCircle, FileSpreadsheet, ArrowLeft } from 'lucide-react';
import StandardNavbar from '../components/StandardNavbar';
import Footer from '../components/Footer';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_API_URL || '';

export default function AdminVouchers() {
  const { user } = useAuth();
  const [vouchers, setVouchers] = useState([]);
  const [lotes, setLotes] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filtros, setFiltros] = useState({ buscar: '', usado: '', lote: '' });
  
  // Estados para carga de CSV
  const [mostrarModal, setMostrarModal] = useState(false);
  const [csvData, setCsvData] = useState(null);
  const [csvPreview, setCsvPreview] = useState([]);
  const [configVoucher, setConfigVoucher] = useState({
    monto: '',
    tipo_descuento: 'fijo',
    fecha_vencimiento: ''
  });
  const [procesando, setProcesando] = useState(false);
  const [mensaje, setMensaje] = useState(null);

  const token = localStorage.getItem('token');

  // Cargar datos iniciales
  useEffect(() => {
    cargarVouchers();
    cargarLotes();
    cargarStats();
  }, [filtros]);

  const cargarVouchers = async () => {
    try {
      const params = new URLSearchParams();
      if (filtros.buscar) params.append('buscar', filtros.buscar);
      if (filtros.usado !== '') params.append('usado', filtros.usado);
      if (filtros.lote) params.append('lote', filtros.lote);

      const res = await fetch(`${API_URL}/admin/vouchers?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      setVouchers(data.vouchers || []);
    } catch (error) {
      console.error('Error cargando vouchers:', error);
    } finally {
      setLoading(false);
    }
  };

  const cargarLotes = async () => {
    try {
      const res = await fetch(`${API_URL}/admin/vouchers/lotes`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      setLotes(data || []);
    } catch (error) {
      console.error('Error cargando lotes:', error);
    }
  };

  const cargarStats = async () => {
    try {
      const res = await fetch(`${API_URL}/admin/vouchers/stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      setStats(data);
    } catch (error) {
      console.error('Error cargando stats:', error);
    }
  };

  // Subir CSV
  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('archivo', file);

    try {
      setMensaje({ tipo: 'info', texto: 'Procesando archivo...' });
      
      const res = await fetch(`${API_URL}/admin/vouchers/upload-csv`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });

      const data = await res.json();

      if (data.success) {
        setCsvData(data.data);
        setCsvPreview(data.preview);
        setMostrarModal(true);
        setMensaje({ tipo: 'success', texto: `${data.total} registros cargados` });
      } else {
        setMensaje({ tipo: 'error', texto: data.error || 'Error procesando CSV' });
      }
    } catch (error) {
      setMensaje({ tipo: 'error', texto: 'Error subiendo archivo' });
    }
    
    // Limpiar input para permitir subir el mismo archivo de nuevo
    e.target.value = '';
  };

  // Procesar y generar vouchers
  const procesarVouchers = async () => {
    if (!csvData || csvData.length === 0) return;
    if (!configVoucher.monto || configVoucher.monto <= 0) {
      setMensaje({ tipo: 'error', texto: 'Ingresa un monto válido' });
      return;
    }

    setProcesando(true);
    try {
      const res = await fetch(`${API_URL}/admin/vouchers/procesar`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          data: csvData,
          ...configVoucher
        })
      });

      const data = await res.json();

      if (data.success) {
        setMensaje({ 
          tipo: 'success', 
          texto: `✅ ${data.creados} vouchers creados (Lote: ${data.lote})` 
        });
        setMostrarModal(false);
        setCsvData(null);
        setCsvPreview([]);
        setConfigVoucher({ monto: '', tipo_descuento: 'fijo', fecha_vencimiento: '' });
        cargarVouchers();
        cargarLotes();
        cargarStats();
      } else {
        setMensaje({ tipo: 'error', texto: data.error });
      }
    } catch (error) {
      setMensaje({ tipo: 'error', texto: 'Error procesando vouchers' });
    } finally {
      setProcesando(false);
    }
  };

  // Toggle usado
  const toggleUsado = async (id) => {
    try {
      const res = await fetch(`${API_URL}/admin/vouchers/${id}/toggle-usado`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) {
        cargarVouchers();
        cargarStats();
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  // Eliminar voucher
  const eliminarVoucher = async (id) => {
    if (!window.confirm('¿Desactivar este voucher?')) return;
    
    try {
      const res = await fetch(`${API_URL}/admin/vouchers/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        cargarVouchers();
        cargarStats();
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  // Exportar CSV
  const exportarCSV = () => {
    const params = filtros.lote ? `?lote=${filtros.lote}` : '';
    window.open(`${API_URL}/admin/vouchers/exportar${params}`, '_blank');
  };

  // Verificar acceso admin
  if (user?.role !== 'admin') {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="bg-white p-8 rounded-lg shadow-lg text-center">
          <h2 className="text-2xl font-bold text-red-600 mb-4">Acceso Denegado</h2>
          <p>No tienes permisos para acceder a esta página.</p>
        </div>
      </div>
    );
  }

  // Descargar template CSV (con tabulador para Excel)
  const descargarTemplate = () => {
    const template = `nombre\tdni\temail\ttelefono`;
    const BOM = '\uFEFF';
    const blob = new Blob([BOM + template], { type: 'text/tab-separated-values;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'template_vouchers.xls';
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <>
      <Helmet>
        <title>Gestión de Vouchers | Noche y Día Turismo</title>
      </Helmet>
      
      <StandardNavbar />
      
      <div className="min-h-screen bg-gray-100 py-8">
        <div className="max-w-7xl mx-auto px-4">
          
          {/* Header */}
          <div className="bg-white rounded-lg shadow-lg p-6 mb-8">
            <div className="flex justify-between items-center">
              <div>
                <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
                  <Gift className="text-cyan-500" size={36} />
                  Gestión de Vouchers
                </h1>
                <p className="text-gray-600 mt-1">Administra vouchers de descuento para clientes</p>
              </div>
              <a 
                href="/admin" 
                className="text-gray-600 hover:text-gray-800 flex items-center gap-2"
              >
                <ArrowLeft size={20} />
                Volver al Admin
              </a>
            </div>
          </div>

          {/* Mensaje */}
          {mensaje && (
            <div className={`mb-6 p-4 rounded-lg flex items-center gap-2 ${
              mensaje.tipo === 'success' ? 'bg-green-100 text-green-800 border border-green-200' :
              mensaje.tipo === 'error' ? 'bg-red-100 text-red-800 border border-red-200' :
              'bg-blue-100 text-blue-800 border border-blue-200'
            }`}>
              <AlertCircle size={20} />
              <span className="flex-1">{mensaje.texto}</span>
              <button onClick={() => setMensaje(null)} className="hover:opacity-70">
                <X size={20} />
              </button>
            </div>
          )}

          {/* Stats */}
          {stats && (
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
              <div className="bg-white p-5 rounded-lg shadow">
                <div className="text-3xl font-bold text-gray-800">{stats.total || 0}</div>
                <div className="text-sm text-gray-500">Total Vouchers</div>
              </div>
              <div className="bg-white p-5 rounded-lg shadow">
                <div className="text-3xl font-bold text-green-600">{stats.disponibles || 0}</div>
                <div className="text-sm text-gray-500">Disponibles</div>
              </div>
              <div className="bg-white p-5 rounded-lg shadow">
                <div className="text-3xl font-bold text-blue-600">{stats.usados || 0}</div>
                <div className="text-sm text-gray-500">Usados</div>
              </div>
              <div className="bg-white p-5 rounded-lg shadow">
                <div className="text-3xl font-bold text-red-600">{stats.vencidos || 0}</div>
                <div className="text-sm text-gray-500">Vencidos</div>
              </div>
              <div className="bg-white p-5 rounded-lg shadow">
                <div className="text-3xl font-bold text-cyan-600">
                  ${Number(stats.monto_pendiente || 0).toLocaleString()}
                </div>
                <div className="text-sm text-gray-500">Monto Pendiente</div>
              </div>
            </div>
          )}

          {/* Acciones */}
          <div className="bg-white rounded-lg shadow-lg p-6 mb-8">
            <h2 className="text-lg font-semibold mb-4">Acciones</h2>
            <div className="flex flex-wrap gap-4">
              <label className="bg-cyan-500 hover:bg-cyan-600 text-white px-6 py-3 rounded-lg cursor-pointer flex items-center gap-2 transition-colors">
                <Upload size={20} />
                Cargar CSV con Clientes
                <input type="file" accept=".csv" onChange={handleFileUpload} className="hidden" />
              </label>
              
              <button 
                onClick={exportarCSV}
                className="bg-green-500 hover:bg-green-600 text-white px-6 py-3 rounded-lg flex items-center gap-2 transition-colors"
              >
                <Download size={20} />
                Exportar Vouchers
              </button>

              <button 
                onClick={descargarTemplate}
                className="bg-gray-500 hover:bg-gray-600 text-white px-6 py-3 rounded-lg flex items-center gap-2 transition-colors"
              >
                <FileSpreadsheet size={20} />
                Descargar Template
              </button>
            </div>
            
            <div className="mt-4 p-4 bg-gray-50 rounded-lg">
              <h3 className="font-medium text-gray-700 mb-2">📋 Formato del CSV:</h3>
              <p className="text-sm text-gray-600">
                El archivo CSV debe tener las columnas: <code className="bg-gray-200 px-1 rounded">nombre</code>, <code className="bg-gray-200 px-1 rounded">dni</code>, <code className="bg-gray-200 px-1 rounded">email</code> (opcional), <code className="bg-gray-200 px-1 rounded">telefono</code> (opcional)
              </p>
              <p className="text-xs text-gray-500 mt-2">
                Ejemplo: Juan Pérez, 12345678, juan@email.com, 261555555
              </p>
            </div>
          </div>

          {/* Filtros */}
          <div className="bg-white p-6 rounded-lg shadow-lg mb-8">
            <h2 className="text-lg font-semibold mb-4">Filtros</h2>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                <input
                  type="text"
                  placeholder="Buscar por nombre, DNI, email o código..."
                  value={filtros.buscar}
                  onChange={(e) => setFiltros({ ...filtros, buscar: e.target.value })}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                />
              </div>
              
              <select
                value={filtros.usado}
                onChange={(e) => setFiltros({ ...filtros, usado: e.target.value })}
                className="px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500"
              >
                <option value="">Todos los estados</option>
                <option value="false">Disponibles</option>
                <option value="true">Usados</option>
              </select>
              
              <select
                value={filtros.lote}
                onChange={(e) => setFiltros({ ...filtros, lote: e.target.value })}
                className="px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500"
              >
                <option value="">Todos los lotes</option>
                {lotes.map(l => (
                  <option key={l.lote} value={l.lote}>
                    {l.lote} ({l.cantidad} vouchers)
                  </option>
                ))}
              </select>
              
              <button
                onClick={() => setFiltros({ buscar: '', usado: '', lote: '' })}
                className="px-4 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Limpiar filtros
              </button>
            </div>
          </div>

          {/* Tabla */}
          <div className="bg-white rounded-lg shadow-lg overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold">
                Vouchers ({vouchers.length})
              </h2>
            </div>
            
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Código</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Nombre</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">DNI</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Email</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Monto</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Vence</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Estado</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Acciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {loading ? (
                    <tr>
                      <td colSpan="8" className="px-6 py-12 text-center text-gray-500">
                        <div className="flex items-center justify-center gap-2">
                          <div className="w-5 h-5 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin"></div>
                          Cargando vouchers...
                        </div>
                      </td>
                    </tr>
                  ) : vouchers.length === 0 ? (
                    <tr>
                      <td colSpan="8" className="px-6 py-12 text-center text-gray-500">
                        <Gift size={48} className="mx-auto mb-4 text-gray-300" />
                        <p>No hay vouchers registrados</p>
                        <p className="text-sm mt-1">Carga un CSV para crear vouchers</p>
                      </td>
                    </tr>
                  ) : vouchers.map(v => (
                    <tr key={v.id} className={`hover:bg-gray-50 ${v.usado ? 'bg-gray-50' : ''}`}>
                      <td className="px-6 py-4 font-mono text-cyan-600 font-bold text-lg">
                        {v.codigo}
                      </td>
                      <td className="px-6 py-4 font-medium">{v.nombre}</td>
                      <td className="px-6 py-4 text-gray-600">{v.dni}</td>
                      <td className="px-6 py-4 text-gray-600 text-sm">{v.email || '-'}</td>
                      <td className="px-6 py-4 font-semibold">
                        {v.tipo_descuento === 'porcentaje' 
                          ? `${v.monto}%` 
                          : `$${Number(v.monto).toLocaleString()}`
                        }
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">
                        {v.fecha_vencimiento 
                          ? new Date(v.fecha_vencimiento).toLocaleDateString('es-AR')
                          : 'Sin vencimiento'
                        }
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                          v.usado 
                            ? 'bg-blue-100 text-blue-800' 
                            : v.fecha_vencimiento && new Date(v.fecha_vencimiento) < new Date()
                              ? 'bg-red-100 text-red-800'
                              : 'bg-green-100 text-green-800'
                        }`}>
                          {v.usado 
                            ? '✓ USADO' 
                            : v.fecha_vencimiento && new Date(v.fecha_vencimiento) < new Date() 
                              ? '✗ VENCIDO' 
                              : '● DISPONIBLE'
                          }
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex gap-2">
                          <button
                            onClick={() => toggleUsado(v.id)}
                            className={`p-2 rounded-lg transition-colors ${
                              v.usado 
                                ? 'text-orange-500 hover:bg-orange-50 border border-orange-200' 
                                : 'text-green-500 hover:bg-green-50 border border-green-200'
                            }`}
                            title={v.usado ? 'Marcar como no usado' : 'Marcar como usado'}
                          >
                            {v.usado ? <X size={18} /> : <Check size={18} />}
                          </button>
                          <button
                            onClick={() => eliminarVoucher(v.id)}
                            className="p-2 rounded-lg text-red-500 hover:bg-red-50 border border-red-200 transition-colors"
                            title="Eliminar voucher"
                          >
                            <Trash2 size={18} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

        </div>
      </div>

      {/* Modal de configuración */}
      {mostrarModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-bold flex items-center gap-2">
                  <FileSpreadsheet className="text-cyan-500" />
                  Configurar Vouchers
                </h2>
                <button 
                  onClick={() => {
                    setMostrarModal(false);
                    setCsvData(null);
                    setCsvPreview([]);
                  }}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <X size={24} />
                </button>
              </div>
              
              <p className="text-gray-600 mb-4">
                Se generarán códigos únicos para <strong className="text-cyan-600">{csvData?.length || 0}</strong> clientes
              </p>

              {/* Preview */}
              <div className="mb-6">
                <h3 className="font-medium mb-2">Vista previa de datos:</h3>
                <div className="max-h-48 overflow-auto border border-gray-200 rounded-lg">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr>
                        <th className="px-3 py-2 text-left font-medium">Nombre</th>
                        <th className="px-3 py-2 text-left font-medium">DNI</th>
                        <th className="px-3 py-2 text-left font-medium">Email</th>
                      </tr>
                    </thead>
                    <tbody>
                      {csvPreview.map((row, i) => (
                        <tr key={i} className="border-t border-gray-100">
                          <td className="px-3 py-2">{row.nombre}</td>
                          <td className="px-3 py-2">{row.dni}</td>
                          <td className="px-3 py-2 text-gray-500">{row.email || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {csvData?.length > 10 && (
                    <div className="text-center text-gray-400 py-3 text-sm bg-gray-50">
                      ... y {csvData.length - 10} registros más
                    </div>
                  )}
                </div>
              </div>

              {/* Configuración */}
              <div className="space-y-4 mb-6">
                <h3 className="font-medium">Configuración del voucher:</h3>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Monto del descuento *
                    </label>
                    <input
                      type="number"
                      value={configVoucher.monto}
                      onChange={(e) => setConfigVoucher({ ...configVoucher, monto: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500"
                      placeholder="Ej: 50000"
                      min="1"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Tipo de descuento
                    </label>
                    <select
                      value={configVoucher.tipo_descuento}
                      onChange={(e) => setConfigVoucher({ ...configVoucher, tipo_descuento: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500"
                    >
                      <option value="fijo">Monto Fijo ($)</option>
                      <option value="porcentaje">Porcentaje (%)</option>
                    </select>
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Fecha de Vencimiento (opcional)
                  </label>
                  <input
                    type="date"
                    value={configVoucher.fecha_vencimiento}
                    onChange={(e) => setConfigVoucher({ ...configVoucher, fecha_vencimiento: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500"
                    min={new Date().toISOString().split('T')[0]}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Si no se especifica, el voucher no tendrá fecha de vencimiento
                  </p>
                </div>
              </div>

              {/* Botones */}
              <div className="flex justify-end gap-3 pt-4 border-t">
                <button
                  onClick={() => {
                    setMostrarModal(false);
                    setCsvData(null);
                    setCsvPreview([]);
                  }}
                  className="px-6 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  onClick={procesarVouchers}
                  disabled={procesando || !configVoucher.monto}
                  className="px-6 py-3 bg-cyan-500 text-white rounded-lg hover:bg-cyan-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
                >
                  {procesando ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      Procesando...
                    </>
                  ) : (
                    <>
                      <Gift size={20} />
                      Generar {csvData?.length} Vouchers
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </>
  );
}